/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/**
 * 完成网络库的一些基础初始化设置
 */
import {
  baseURL, isH5App, newGatewayUrl, sysTag, isFatEnv, gatewayUrl,
} from '@/constant';
import request, { AxiosRequestConfig } from '@hb/sharp-request';
import { getAuthToken, updateModuleState } from '@/store';
import { Toast, ToastType } from '@hb/coffee';
import platform from '@/shared/utils/platform';
import { RouterH5Enum, WHITE_LIST } from '@/router/router-names';
import router from '@/router';
import { ErrorTypeEnum } from '@/enums/codeError';
import Bootstrap from '../bootstrap';
import { formatToastOption } from '../formatToast';
import { getIsPageWillReloadSymbol } from '../pageControl';
import { checkThisTransitionHasRetry, requestRetryDialog, requestRetryWrapHandler } from './errorRetryDialog';
import { landData } from '../landData';

interface ErrorType extends Error {code:number}

export interface AbortControllerType {
  /** 请求名 */
  action: string,
  /** AbortController */
  controller: AbortController
}
/** 当前页需要取消请求 - AbortController集合 */
const requestControllers: AbortControllerType[] = [];

/** 取消接口请求 */
export const abortRequest = ():void => {
  if (requestControllers.length) {
    requestControllers.forEach(({ controller }) => {
      controller.abort();
    });
    requestControllers.splice(0);
  }
};

/**
  * 完成网络库的初始化设置
  */
export const initRequest = (() => {
  // 标记是否已经做过初始化
  let hasInitialized = false;

  return () => {
    if (!hasInitialized) {
      hasInitialized = true;

      request.setDefaults({
        loadingMessage: '加载中...',
        baseURL,
        autoLoading: true,
        getToken: () => getAuthToken(),
        showErrMsg: (message: string, error, config?: AxiosRequestConfig) => {
          // 在h5场景下 当前接口token失效 且 页面需要登录
          if (error && (error as ErrorType).code
              === ErrorTypeEnum.LOGIN_OVERDUE) {
            // 精确匹配当前的router值
            const hrefUrl = window.location.hash.split('?')[0].replace(/(#)/g, '');
            const notNeedLogin = Object.keys(WHITE_LIST)
              .find((item) => hrefUrl === item);
            /** 当页面不需要登录态 or 即将完整刷新页面(比如：账号注销成功返回首页)，则不展示登录态异常toast */
            if (!notNeedLogin && !getIsPageWillReloadSymbol()) {
              // h5 token 置空
              Bootstrap.initLoginUser(
                Object.create(null),
                Object.create(null),
              );
              // h5 token 失效时，跳转登录页
              if (!(hrefUrl === RouterH5Enum.login) && isH5App) {
                router.push({
                  name: RouterH5Enum.login,
                });
              }
              // 如果不是h5,抛出token失效的异常
              if (!isH5App) {
                Toast.fail(formatToastOption(message));
              }
              return;
            }
            return;
          }
          if (error && (error as ErrorType).code
              === ErrorTypeEnum.LOG_OUT) {
            // 用户已注销账号
            updateModuleState('base', {
              isLogoutUser: true,
            });
          }

          /** 若当前请求配置了errorRetryDialog，且该次请求未标记为已重试，则进入重新请求流程 */
          if (config && config.errorRetryDialog && !checkThisTransitionHasRetry(config)) {
            requestRetryDialog(config);
            return;
          }

          Toast.fail(formatToastOption(message));
        },
        showLoading: (message: string) => {
          const ToastInst = Toast.loading({
            message,
            duration: 15000,
            forbidClick: true,
          }) as { state: { type: ToastType } };

          return {
            message,
            clear: () => {
              // 新增 toast 类型判断，当是 loading 类型 再去 clear
              // 以解决 loading 过程中，接口失败触发Toast.Fail时，被clear，导致 toast 时长太短的问题 https://jira.hellobike.cn/browse/FPROJECT-8180
              if (ToastInst.state.type === 'loading') {
                Toast.clear();
              }
            },
          };
        },
        // timeout: 15000 //修改超时时间
      });

      request.axios.interceptors.request.use((
        config,
      ) => {
        /** 防止 data 为undefined，造成后续代码异常 */
        if (!config.data) return config;
        const { action } = config.data as { action: string};
        /** 是否需要取消请求
         * 如果 config.signal 存在，则不创建新的 AbortController，直接使用传入的 signal
         */
        if (!config.signal && config.abortXhr && window.AbortController) {
          const controller = new AbortController();
          config.signal = controller.signal;
          requestControllers.push({
            action,
            controller,
          });
        }

        // 此处是将main中逻辑移动至此
        if (isFatEnv
          && sysTag
          && config.baseURL
          && [gatewayUrl, newGatewayUrl].includes(config.baseURL)) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          config.data = { __sysTag: sysTag, ...config.data };
        }
        if (config.data.action) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          config.data = {
            ...platform.currentPlatformType,
            /**
             * 新增通用source字段，便于快速分析用户来源.
             * 若接口已使用source字段，则不设置了
             */
            source: landData.getSouce(),
            ...config.data,
          };
          config.baseURL = `${config.baseURL as string}?action=${config.data.action as string}`;
        }

        return config;
      });
    }
  };
})();

export default {
  postGate<T = unknown>(
    action: string,
    data?: Dictionary,
    config: AxiosRequestConfig = {},
  ): Promise<T> {
    initRequest();
    /** 设置请求接口：transitionId */
    const configReAssign = {
      ...config,
      transitionId: `${action}_${Date.now()}_${Math.floor(Math.random() * 90) + 10}`,
    };
    // 兼容 当前未设置baseURL的接口请求
    if (!config || (config && !config.baseURL)) {
      Object.assign(configReAssign, {
        baseURL: newGatewayUrl,
      });
    }
    return request.postGate(action, data, {
      ...configReAssign,
    });
  },
  requestRetryWrapHandler,
};
